import { Client, type Room } from "colyseus.js";
import { DEFAULT_COSMETICS, JOIN_LOAD_READY_OPT } from "@neon-purge/shared";
import { api } from "./api.js";
import { isTouchDevice } from "../game/touch.js";

function endpoint(): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/game`;
}

async function makeClient(): Promise<Client> {
  // access tokens live 15 min — sitting on the menus longer than that made
  // every join fail until some REST call happened to refresh. Refresh first.
  await api.ensureFreshToken();
  const client = new Client(endpoint());
  client.auth.token = api.token ?? "";
  return client;
}

/** Equipped cosmetics sent as join options (server validates → hex). */
function joinCosmetics(): Record<string, string> {
  return { ...DEFAULT_COSMETICS, ...(api.account?.cosmetics ?? {}) };
}

/** Translate raw colyseus/network failures ("seat reservation expired",
 *  "maxClients reached"…) into messages a player can act on. */
function friendlyRoomError(err: unknown, ctx: "join" | "create"): Error {
  const raw = String((err as Error)?.message ?? err ?? "").trim();
  const lower = raw.toLowerCase();
  let msg: string;
  if (lower.includes("maxclients") || lower.includes("is full") || lower.includes("locked")) {
    msg = "That room is full.";
  } else if (lower.includes("not found") || lower.includes("no rooms") || lower.includes("disposed") || lower.includes("has been disposed")) {
    msg = "That room no longer exists — it emptied out and closed.";
  } else if (lower.includes("seat reservation")) {
    msg = "Join timed out while reserving your seat — try again.";
  } else if (lower.includes("unauthorized") || lower.includes("onauth") || lower.includes("token") || lower.includes("jwt")) {
    msg = "Your session expired — sign in again.";
  } else if (lower.includes("failed to fetch") || lower.includes("network") || lower.includes("websocket") || lower.includes("timed out") || lower.includes("econn")) {
    msg = "Can't reach the game server — check your connection and try again.";
  } else {
    msg = `${ctx === "create" ? "Couldn't create the room" : "Couldn't join"}${raw ? ` (${raw})` : ""}.`;
  }
  return new Error(msg);
}

export interface RoomListing {
  roomId: string;
  clients: number;
  maxClients: number;
  metadata?: { code?: string; mapId?: string };
}

/** Public room browser (served by the gameserver's /rooms endpoint). */
export async function listRooms(): Promise<RoomListing[]> {
  const res = await fetch("/game/rooms");
  if (!res.ok) throw new Error("room list unavailable");
  return (await res.json()) as RoomListing[];
}

function joinOpts(extra: Record<string, unknown> = {}): Record<string, unknown> {
  const o: Record<string, unknown> = { cosmetics: joinCosmetics(), ...extra };
  // LOAD READY: MatchMode sends ClientMsg.Ready when its load screen closes, so
  // the server may protect + hold us until then. NOT set for local split-screen
  // guest seats (joinAsLocalGuest) — they never own a load screen.
  o[JOIN_LOAD_READY_OPT] = true;
  if (api.account?.skinUrl) o.skinUrl = api.account.skinUrl;
  if (isTouchDevice()) o.mobile = true; // 📱 nameplate badge for phone players
  // LOADOUT PERK. Sent as a join OPTION, not only as a message: onJoin builds
  // the player's hp/grenades before any client message can arrive, so a
  // quickplay joiner who skips the lobby would otherwise play their first life
  // without the perk they picked. The lobby row also sends `ClientMsg.Loadout`,
  // which is what handles a change made after joining.
  try {
    const ld = localStorage.getItem("neonpurge.loadout");
    if (ld) o.loadout = ld;
    const ft = Number(localStorage.getItem("neonpurge.flagsTeam"));
    if (ft === 1 || ft === 2) o.preferredTeam = ft;
  } catch { /* storage disabled — plays with no perk, which is valid */ }
  return o;
}

export async function joinQuickPlay(opts: { mapId?: string; mode?: string; purgeTeam?: boolean; modifier?: string } = {}): Promise<Room> {
  // explicit hub value: filterBy only segregates when the option is PRESENT —
  // without it, quickplay would get matchmade into the public hangout room.
  // Pass the chosen map/mode so a freshly-created quick room uses them instead
  // of the default (which was always the practice field).
  try {
    return await (await makeClient()).joinOrCreate("gungame", joinOpts({ hub: "quick", ...opts }));
  } catch (err) { throw friendlyRoomError(err, "join"); }
}

/** THE public hangout room — always joinable; first one in becomes owner,
 *  ownership hands off as people leave, drop in/out freely. */
export async function joinPublicRoom(): Promise<Room> {
  try {
    return await (await makeClient()).joinOrCreate("gungame", joinOpts({ hub: "public", lobby: true }));
  } catch (err) { throw friendlyRoomError(err, "join"); }
}

export async function createRoom(options: {
  mapId?: string;
  maxPlayers?: number;
  isPrivate?: boolean;
  ladderId?: string;
  roundTimeS?: number;
  bots?: number;
  mode?: string;
  difficulty?: string;
  vehicles?: boolean;
  teams?: boolean;
  monsters?: boolean;
  nightCycle?: boolean;
  noAutoAim?: boolean;
  /** ROUND MODIFIER id ("" / omitted = standard rules). */
  modifier?: string;
  /** Community custom map (a MapDef) — hosts a multiplayer room on it. */
  customMap?: unknown;
}): Promise<Room> {
  // created rooms open in a lobby (owner picks map/bots + starts)
  try {
    return await (await makeClient()).create("gungame", joinOpts({ ...options, lobby: true }));
  } catch (err) { throw friendlyRoomError(err, "create"); }
}

/** Solo vs AI: a private room seeded with bots, on the given map/ladder. */
export async function createSoloRoom(options: {
  mapId?: string;
  roundTimeS?: number;
  ladderId?: string;
  bots?: number;
  mode?: string;
  purgeTeam?: boolean;
  difficulty?: string;
  vehicles?: boolean;
  teams?: boolean;
  monsters?: boolean;
  nightCycle?: boolean;
  noAutoAim?: boolean;
  /** ROUND MODIFIER id ("" / omitted = standard rules). */
  modifier?: string;
  passiveBots?: boolean;
  botTeams?: number[];
  myTeam?: number;
  /** Community custom map (a MapDef), passed straight to the server. */
  customMap?: unknown;
}): Promise<Room> {
  try {
    return await (await makeClient()).create(
      "gungame",
      joinOpts({ ...options, bots: options.bots ?? 1, isPrivate: true }),
    );
  } catch (err) { throw friendlyRoomError(err, "create"); }
}

/** MULTIPLAYER LOCAL seat (P2-P4): a fresh guest identity per seat — its own
 *  token and websocket, so the server sees a real extra player. Guests are
 *  deliberate here: unique usernames (no same-account seat-bumping games) and
 *  the server skips stat writes for them, so couch matches never farm P1's
 *  account stats. Does NOT touch the global `api` singleton — P1 stays
 *  signed in as themselves. */
export async function joinAsLocalGuest(roomId: string, cosmetics: Record<string, string>): Promise<Room> {
  const res = await fetch("/api/guest", { method: "POST" });
  if (!res.ok) throw new Error("couldn't get a guest seat token");
  const { accessToken } = (await res.json()) as { accessToken: string };
  const client = new Client(endpoint());
  client.auth.token = accessToken;
  try {
    return await client.joinById(roomId, { cosmetics: { ...DEFAULT_COSMETICS, ...cosmetics } });
  } catch (err) { throw friendlyRoomError(err, "join"); }
}

export async function joinById(roomId: string, replaceSessionId?: string): Promise<Room> {
  try {
    return await (await makeClient()).joinById(roomId, joinOpts(
      replaceSessionId ? { replaceSessionId } : {},
    ));
  } catch (err) { throw friendlyRoomError(err, "join"); }
}

/** Resolve a human-friendly code via the api service, then join. */
export async function joinByCode(code: string): Promise<Room> {
  const res = await fetch(`/api/rooms/${encodeURIComponent(code)}`, {
    headers: { authorization: `Bearer ${api.token}` },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? "room not found");
  }
  const { roomId } = (await res.json()) as { roomId: string };
  return joinById(roomId);
}
